/**
 * Party mode: listens through the device's microphone (which hears whatever music is playing nearby)
 * and reports the loudness and the kick drum. Nothing is recorded, stored or sent anywhere: the
 * samples are only analysed in memory and dropped.
 */
export interface MusicFrame {
    /** 0..1 overall loudness, smoothed. */
    level: number;
    /** True on the frame a bass hit is detected. */
    beat: boolean;
}

const MIN_BEAT_GAP_MS = 260;
/** A bass hit has to beat the recent average by this much. */
const BEAT_RATIO = 1.35;
/** Below this the room is considered quiet and nothing is reported. */
const NOISE_FLOOR = 0.06;

export class MusicListener {
    private stream: MediaStream | null = null;
    private ctx: AudioContext | null = null;
    private raf = 0;

    static supported(): boolean {
        return (
            typeof navigator !== 'undefined' &&
            !!navigator.mediaDevices?.getUserMedia
        );
    }

    get active() {
        return this.stream !== null;
    }

    /** Resolves false when the person says no or there is no microphone. */
    async start(onFrame: (f: MusicFrame) => void): Promise<boolean> {
        if (this.stream || !MusicListener.supported()) return this.active;
        try {
            // Echo cancellation and gain control would flatten exactly the beat we want.
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                },
            });
        } catch {
            return false;
        }
        const Ctor: typeof AudioContext | undefined =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;
        if (!Ctor) {
            this.stop();
            return false;
        }
        const ctx = new Ctor();
        this.ctx = ctx;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.5;
        // Deliberately not connected to the speakers.
        ctx.createMediaStreamSource(this.stream).connect(analyser);

        const bins = new Uint8Array(analyser.frequencyBinCount);
        const binHz = ctx.sampleRate / analyser.fftSize;
        const bassEnd = Math.max(2, Math.round(200 / binHz));
        let avgBass = 0;
        let level = 0;
        let lastBeat = 0;

        const loop = () => {
            analyser.getByteFrequencyData(bins);
            let bass = 0;
            for (let i = 1; i < bassEnd; i++) bass += bins[i];
            bass /= (bassEnd - 1) * 255;
            let all = 0;
            for (let i = 1; i < 200; i++) all += bins[i];
            all /= 199 * 255;

            level += (Math.min(1, all * 2.5) - level) * 0.25;
            const now = performance.now();
            const beat =
                bass > NOISE_FLOOR &&
                bass > avgBass * BEAT_RATIO &&
                now - lastBeat > MIN_BEAT_GAP_MS;
            if (beat) lastBeat = now;
            avgBass += (bass - avgBass) * 0.05;
            onFrame({ level: bass > NOISE_FLOOR ? level : 0, beat });
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
        return true;
    }

    stop() {
        cancelAnimationFrame(this.raf);
        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = null;
        void this.ctx?.close();
        this.ctx = null;
    }
}
